import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch, type Ref } from 'vue';

import type { PhxsgTabsOrientation, PhxsgTabsPanelData } from '../tabs.types';

interface UseTabContentContext {
  emit: (event: 'tab-content-change', element: HTMLElement) => void;
}

export function useTabContent(
  {
    currentPanel,
    panels,
    orientation,
    contentOffset,
  }: {
    currentPanel: Ref<PhxsgTabsPanelData | null>;
    panels: Ref<PhxsgTabsPanelData[]>;
    orientation: Ref<PhxsgTabsOrientation>;
    contentOffset: Ref<number>;
  },
  ctx: UseTabContentContext,
) {
  const wrapperHeight = ref(0);
  const calculatedContentOffset = ref('0');
  const vNodeRef = ref<{ tabElement?: HTMLElement | null } | null>(null);
  const currentPanelId = computed(() => currentPanel.value?.id ?? null);
  let observedElement: HTMLElement | null = null;

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (Array.isArray(entry.borderBoxSize) && entry.borderBoxSize.length > 0) {
        wrapperHeight.value = entry.borderBoxSize[0].blockSize;
      } else if (entry.contentRect) {
        wrapperHeight.value = entry.contentRect.height;
      }
    }
  });

  function observeCurrentPanel() {
    const currentElement = vNodeRef.value?.tabElement ?? null;

    if (observedElement && observedElement !== currentElement) {
      resizeObserver.unobserve(observedElement);
    }

    observedElement = currentElement;

    if (currentElement) {
      ctx.emit('tab-content-change', currentElement);
      resizeObserver.observe(currentElement);
    }
  }

  function calcContentOffset(currentId: string | null | undefined, previousId: string | null | undefined) {
    if (!currentId || !previousId || panels.value.length === 0) {
      calculatedContentOffset.value = '0';

      return;
    }

    const previousIndex = panels.value.findIndex(({ id }) => id === previousId);
    const currentIndex = panels.value.findIndex(({ id }) => id === currentId);
    const offsetValue = currentIndex > previousIndex ? -contentOffset.value : contentOffset.value;

    calculatedContentOffset.value = orientation.value === 'vertical' ? `${offsetValue}px` : `${offsetValue}%`;
  }

  onMounted(() => {
    void nextTick(() => {
      window.setTimeout(() => {
        observeCurrentPanel();
      });
    });
  });

  onUpdated(() => {
    void nextTick(() => {
      window.setTimeout(() => {
        observeCurrentPanel();
      });
    });
  });

  onBeforeUnmount(() => {
    if (observedElement) {
      resizeObserver.unobserve(observedElement);
    }

    resizeObserver.disconnect();
  });

  watch(
    currentPanelId,
    (newValue, oldValue) => {
      calcContentOffset(newValue, oldValue);
    },
    { immediate: true },
  );

  return {
    vNodeRef,
    wrapperHeight,
    calculatedContentOffset,
  };
}
